import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

/**
 * The app's one STOMP connection. Everything realtime rides it: notifications,
 * maintenance mode, work-order rows, kiosk screens and the job planner.
 *
 * Replaces polling. A poll is the client repeatedly asking a question only the server can
 * answer, when the server already knows the answer; three 60-second polls per tab were
 * most of this app's production request rate.
 *
 * There used to be three of these — this one plus `JobPlannerWebSocket` and
 * `KioskWebSocket`, each with its own socket and its own hand-rolled reconnect loop, and
 * neither of those re-established its subscriptions afterwards. Both are now thin
 * vocabulary adapters over this client, so there is one reconnect implementation to be
 * right rather than three to keep in step.
 *
 * Because the connection is shared and session-scoped, **a screen must never call
 * `disconnect()`** — that would take the notification bell down with it. Screens
 * subscribe on mount and unsubscribe on unmount; nothing connects per screen.
 *
 * Prefer the `useRealtimeResource` hook over subscribing directly: it encodes the
 * contract below so you cannot forget a piece of it.
 *
 * **Two things every consumer must do**, because push alone is not enough:
 *
 *  1. **Fetch current state on mount.** A broadcast only carries *changes*, so it can
 *     never tell you what was already true before you connected.
 *  2. **Re-fetch on every (re)connect** via `onConnect`. Anything published while the
 *     socket was down was missed and no replay is coming — this is the whole reason
 *     `onConnect` fires on reconnects and not just the first connect.
 *
 * Consumers should also keep their poll as a fallback for when the socket cannot
 * connect at all (a proxy blocking WebSocket, SockJS falling back and failing), but
 * skip the poll while `isConnected()` — see NotificationContext for the pattern.
 */
/**
 * Exported so a genuinely different *identity* can have its own connection — currently only
 * the kiosk, which authenticates as a station rather than a user and so must use a different
 * endpoint. This is not licence to open a second connection for a second screen: two
 * connections for one logged-in user is the duplication this file exists to have removed.
 */
export class RealtimeClient {
  constructor(url = null) {
    this.client = null;
    this.connected = false;
    this.overrideUrl = url;
    // destination -> Set<handler>. Kept independently of the STOMP subscriptions so
    // they can be re-established after a reconnect without consumers re-subscribing.
    this.handlers = new Map();
    this.stompSubscriptions = new Map();
    this.connectListeners = new Set();
    // Fired with a boolean on every transition, for screens that show connection state.
    // Distinct from connectListeners, which exist for the re-fetch-on-reconnect rule and
    // must not fire on disconnect.
    this.connectionChangeListeners = new Set();
    this.url = url || import.meta.env.VITE_WS_URL || 'http://localhost:8080/ws';
  }

  /**
   * Point this client at a different URL. Only meaningful before `connect()`, or followed
   * by a reconnect — the kiosk uses it to append its station credentials, which aren't
   * known until the screen has been paired.
   */
  setUrl(url) {
    this.url = url;
  }

  isConnected() {
    return this.connected;
  }

  connect() {
    if (this.client) {
      return;
    }

    this.client = new Client({
      webSocketFactory: () => new SockJS(this.url),
      // The library reconnects for us; 5s is slow enough not to hammer a server that
      // is restarting, fast enough that a deploy doesn't leave the bell dead for long.
      reconnectDelay: 5000,
      heartbeatIncoming: 30000,
      heartbeatOutgoing: 30000,
      debug: () => {},
      onConnect: () => {
        this.connected = true;
        // Re-establish every subscription: a reconnect is a brand new STOMP session
        // and the broker remembers nothing about the old one.
        this.handlers.forEach((_, destination) => this.openSubscription(destination));
        // Tell consumers to re-fetch. Fires on reconnects too, by design — that is
        // how they recover the messages they missed while the socket was down.
        this.connectListeners.forEach((listener) => {
          try {
            listener();
          } catch (err) {
            console.error('Realtime onConnect listener failed:', err);
          }
        });
        this.notifyConnectionChange(true);
      },
      onDisconnect: () => {
        this.connected = false;
        this.notifyConnectionChange(false);
      },
      onWebSocketClose: () => {
        this.connected = false;
        this.stompSubscriptions.clear();
        this.notifyConnectionChange(false);
      },
      onStompError: (frame) => {
        console.error('Realtime STOMP error:', frame?.headers?.message);
      },
    });

    this.client.activate();
  }

  disconnect() {
    if (!this.client) {
      return;
    }
    this.handlers.clear();
    this.stompSubscriptions.clear();
    this.connectListeners.clear();
    this.connectionChangeListeners.clear();
    this.connected = false;
    this.client.deactivate();
    this.client = null;
  }

  notifyConnectionChange(isConnected) {
    this.connectionChangeListeners.forEach((listener) => {
      try {
        listener(isConnected);
      } catch (err) {
        console.error('Realtime connection listener failed:', err);
      }
    });
  }

  /**
   * Observe connection state, for screens that display it. Called immediately with the
   * current state so a listener registered after connect isn't left showing "offline".
   * Returns a remover.
   */
  onConnectionChange(listener) {
    this.connectionChangeListeners.add(listener);
    listener(this.connected);
    return () => { this.connectionChangeListeners.delete(listener); };
  }

  openSubscription(destination) {
    if (!this.client?.connected || this.stompSubscriptions.has(destination)) {
      return;
    }
    const sub = this.client.subscribe(destination, (message) => {
      let payload = null;
      try {
        payload = message.body ? JSON.parse(message.body) : null;
      } catch {
        // A non-JSON frame is a server-side mistake, not something to crash the tab over.
        console.error('Realtime: unparseable message on', destination);
        return;
      }
      // "Is it arriving?" is the first question every time something realtime looks
      // broken, and without this the only way to answer it is reading raw WS frames in
      // devtools. Dev only — this would be noise in production.
      if (process.env.NODE_ENV === 'development') {
        console.debug('📡 Realtime message on', destination, payload);
      }
      this.handlers.get(destination)?.forEach((handler) => {
        try {
          handler(payload);
        } catch (err) {
          console.error('Realtime handler failed for', destination, err);
        }
      });
    });
    this.stompSubscriptions.set(destination, sub);
  }

  /**
   * Subscribe to a destination. Safe to call before the socket is connected — the
   * subscription is opened on connect. Returns an unsubscribe function.
   */
  subscribe(destination, handler) {
    if (!this.handlers.has(destination)) {
      this.handlers.set(destination, new Set());
    }
    this.handlers.get(destination).add(handler);
    this.openSubscription(destination);

    return () => {
      const set = this.handlers.get(destination);
      if (!set) {
        return;
      }
      set.delete(handler);
      if (set.size === 0) {
        this.handlers.delete(destination);
        this.stompSubscriptions.get(destination)?.unsubscribe();
        this.stompSubscriptions.delete(destination);
      }
    };
  }

  /** Run `listener` on every successful connect, including reconnects. Returns a remover. */
  onConnect(listener) {
    this.connectListeners.add(listener);
    if (this.connected) {
      listener();
    }
    // Returns void, not Set.delete's boolean — this remover is routinely returned
    // straight from a useEffect, and React rejects a destructor that returns a value.
    return () => { this.connectListeners.delete(listener); };
  }

  /**
   * Publish to an `/app/...` destination.
   *
   * **Drops when disconnected** rather than queueing, and returns false so the caller
   * knows. This is deliberate: everything sent over this socket is a statement about
   * *now* — a planner lock, a presence ping, a kiosk submission. Replaying one after a
   * thirty-second gap asserts something that has since stopped being true, which is worse
   * than never sending it. Anything that must survive a disconnect belongs on HTTP, where
   * it gets a transaction and a response.
   */
  send(destination, body = {}) {
    if (!this.client?.connected) {
      console.warn('Realtime: dropped send to', destination, '— not connected');
      return false;
    }
    try {
      this.client.publish({ destination, body: JSON.stringify(body) });
      return true;
    } catch (err) {
      console.error('Realtime: send to', destination, 'failed', err);
      return false;
    }
  }
}

const hubRealtime = new RealtimeClient();

/**
 * Destinations the backend publishes to. Mirrors `RealtimeChannels.java`.
 *
 * These two lists are the one part of this system that can still drift silently: a
 * subscribe to a destination nothing publishes to is not an error on either side, it is
 * just permanent silence. Change one, change the other.
 */
export const REALTIME_DESTINATIONS = {
  notifications: '/user/queue/notifications',
  maintenanceMode: '/topic/maintenance-mode',
  workOrderRows: '/topic/work-orders/rows',
  kioskCommands: (stationCode) => `/topic/kiosk/${stationCode}`,
  kioskResponses: (stationCode) => `/topic/kiosk/${stationCode}/response`,
  planner: (plannerId) => `/topic/planner/${plannerId}`,
};

/** `/app/...` destinations the backend has @MessageMapping handlers for. */
export const REALTIME_SEND = {
  plannerJoin: (plannerId) => `/app/planner/${plannerId}/join`,
  plannerLeave: (plannerId) => `/app/planner/${plannerId}/leave`,
  plannerLock: (plannerId) => `/app/planner/${plannerId}/lock`,
  plannerUnlock: (plannerId) => `/app/planner/${plannerId}/unlock`,
  plannerUpdate: (plannerId) => `/app/planner/${plannerId}/update`,
  plannerPing: (plannerId) => `/app/planner/${plannerId}/ping`,
  kioskSubmit: (stationCode) => `/app/kiosk/${stationCode}/submit`,
  kioskPing: (stationCode) => `/app/kiosk/${stationCode}/ping`,
};

export default hubRealtime;
